import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import PartySocket from "partysocket";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

import type { KnowledgeDocumentWithTask } from "./types/Document";

interface DocumentMetadataProps {
  document: KnowledgeDocumentWithTask;
  isOpen: boolean;
  onClose: () => void;
  partySocket: PartySocket;
}

interface StatusHistoryEntry {
  status: string;
  timestamp: string;
}

interface ProcessingInfo {
  totalPages: number;
  totalChunks: number;
  chunks: Array<{
    pageNumber: number;
    chunkCount: number;
  }>;
  processedAt: string;
  contentType: string;
}

interface DocumentMetadataInfo {
  id: string;
  key: string;
  name: string;
  contentType: string;
  uploadedAt: string;
  status: string;
  updatedAt: string;
  statusHistory: StatusHistoryEntry[];
  processing: ProcessingInfo;
}

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  } catch {
    return "-";
  }
};

const StatusIndicator = ({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-500";
      case "queued":
        return "bg-yellow-500";
      case "processing":
        return "bg-blue-500";
      case "error":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`h-2 w-2 rounded-full ${getStatusColor(status)}`} />
      <span className="capitalize">{status}</span>
    </div>
  );
};

export function DocumentMetadata({
  document,
  isOpen,
  onClose,
  partySocket,
}: Readonly<DocumentMetadataProps>) {
  const [isReindexing, setIsReindexing] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: metadata,
    isLoading,
    error,
  } = useQuery<DocumentMetadataInfo>({
    queryKey: ["document-metadata", document.id],
    queryFn: async () => {
      const response = await PartySocket.fetch(
        {
          host: partySocket.host,
          party: "main",
          room: document.orgId ?? "",
          path: "api/knowledge/metadata",
        },
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ documentId: document.id }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch document metadata");
      }

      return response.json();
    },
    enabled: isOpen,
    retry: 2,
  });

  const reindexMutation = useMutation({
    mutationFn: async () => {
      setIsReindexing(true);
      const response = await PartySocket.fetch(
        {
          host: partySocket.host,
          party: "main",
          room: document.orgId ?? "",
          path: "api/knowledge/reindex",
        },
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ documentId: document.id }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to reindex document");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["document-metadata", document.id],
      });
    },
    onSettled: () => {
      setIsReindexing(false);
    },
  });

  const hasProcessingInconsistency =
    metadata?.status === "queued" && metadata.processing?.processedAt;
  const processedAfterLastUpdate =
    metadata?.processing?.processedAt &&
    metadata.updatedAt &&
    new Date(metadata.processing.processedAt) > new Date(metadata.updatedAt);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Document Metadata</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => reindexMutation.mutate()}
              disabled={isReindexing}
            >
              {isReindexing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Reindex
            </Button>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1">
          <div className="grid gap-4 p-1">
            {hasProcessingInconsistency && (
              <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3 text-sm text-yellow-700 dark:text-yellow-400">
                <p>
                  Status inconsistency detected: Document appears to be
                  processed but status is still queued.
                </p>
                {processedAfterLastUpdate && (
                  <p className="mt-1">
                    Processing completed after last status update. Try
                    refreshing or reindexing.
                  </p>
                )}
              </div>
            )}
            <div>
              <h3 className="mb-2 font-medium">Document Info</h3>
              <div className="rounded-lg bg-muted p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="font-medium text-muted-foreground">Name</p>
                    <p className="truncate">{metadata?.name ?? "-"}</p>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">Status</p>
                    {metadata?.status ? (
                      <StatusIndicator status={metadata.status} />
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">
                      Uploaded At
                    </p>
                    <p>{formatDate(metadata?.uploadedAt)}</p>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">
                      Last Updated
                    </p>
                    <p>{formatDate(metadata?.updatedAt)}</p>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground">
                      Content Type
                    </p>
                    <p>{metadata?.contentType ?? "application/pdf"}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="mb-2 font-medium">Processing Info</h3>
              {isLoading ? (
                <div className="flex items-center justify-center rounded-lg bg-muted p-4">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading metadata...</span>
                </div>
              ) : error ? (
                <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
                  Failed to load metadata. Please try reindexing the document.
                </div>
              ) : metadata?.processing ? (
                <div className="rounded-lg bg-muted p-4">
                  <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-muted-foreground">
                        Total Pages
                      </p>
                      <p>{metadata.processing.totalPages ?? "-"}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">
                        Total Chunks
                      </p>
                      <p>{metadata.processing.totalChunks ?? "-"}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-medium text-muted-foreground">
                        Processed At
                      </p>
                      <p>{formatDate(metadata.processing.processedAt)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-muted-foreground">
                      Chunks per Page
                    </p>
                    <div className="space-y-1">
                      {metadata.processing.chunks?.length ? (
                        metadata.processing.chunks.map((chunk) => (
                          <div
                            key={`page-${chunk.pageNumber}`}
                            className="flex items-center justify-between rounded bg-background p-2 text-xs"
                          >
                            <span>Page {chunk.pageNumber}</span>
                            <span>{chunk.chunkCount} chunks</span>
                          </div>
                        ))
                      ) : (
                        <div className="rounded bg-background p-2 text-xs text-muted-foreground">
                          No chunk information available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                  No processing information available
                </div>
              )}
            </div>
            <div>
              <h3 className="mb-2 font-medium">Status History</h3>
              <div className="rounded-lg bg-muted p-4">
                <div className="space-y-1">
                  {metadata?.statusHistory?.length ? (
                    metadata.statusHistory.map((entry, index) => (
                      <div
                        key={`item-${index}`}
                        className="flex items-center justify-between rounded bg-background p-2 text-xs"
                      >
                        <span className="capitalize">{entry.status}</span>
                        <span>{formatDate(entry.timestamp)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="rounded bg-background p-2 text-xs text-muted-foreground">
                      No status history available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
